import { useAppSelector } from "../../app/hooks";
import { type IHotelProduct } from "../../features/hotel/hotelInterface";
import { selectHotel } from "../../features/hotel/hotelSlice";
import {
  GTMTrackingRemoveProduct,
  GTMTrackingSelectProduct,
} from "../../helpers/GTMTracking";
import Button from "../Button";
import { useTranslation } from "react-i18next";
import FormatPrice from "../Helpers/FormatPrice";

interface IProps {
  Product: IHotelProduct;
  productIds: string[];
  toggleProductId: (productId: string) => void;
}

function RoomExtraProductCard({
  Product,
  productIds,
  toggleProductId,
}: IProps) {
  const hotel = useAppSelector(selectHotel);
  const { t } = useTranslation();

  const addProduct = (p: IHotelProduct) => {
    GTMTrackingSelectProduct(p.Id);
    toggleProductId(p.Id);
  };

  const removeProduct = (id: IHotelProduct["Id"]) => {
    GTMTrackingRemoveProduct();
    toggleProductId(id);
  };

  const hasProduct = (id: IHotelProduct["Id"]): boolean => {
    return productIds.includes(id);
  };

  return (
    <>
      <div className="bg-custom-100 rounded-xl text-black text-fluid-base flex flex-col w-full p-4 gap-4">
        <div className="grid grid-cols-3 gap-4 cursor-pointer">
          <div className="col-span-2 flex flex-col gap-2">
            <span className=" text-fluid-h3 leading-none">
              {Product.Name[hotel.Culture.LanguageCode] ||
                Product.Name["en-US"]}
            </span>
            <span className="text-fluid-base text-left leading-none">
              <FormatPrice
                price={Product.Prices[hotel.Culture.CurrencyCode]}
                competitorPrice={false}
              />{" "}
              {Product.Charging === "PerPersonNight" &&
                t("scenes.extras.perPersonPerNight")}
            </span>
            <p className="text-black opacity-50 text-fluid-base">
              {Product.Description[hotel.Culture.LanguageCode] ||
                Product.Description["en-US"]}{" "}
            </p>
          </div>
          <div className="relative cursor-pointer">
            {Product.ImageId && (
              <img
                className="rounded-lg aspect-4/3 object-cover"
                src={`${hotel.ImageBaseUrl}/${Product.ImageId}?Mode=0&Height=240&Width=600`}
                alt={
                  Product.Name[hotel.Culture.LanguageCode] ||
                  Product.Name["en-US"]
                }
              />
            )}
          </div>
        </div>
        <div className="mt-4">
          {!hasProduct(Product.Id) && (
            <Button
              text={`${t("scenes.extras.add")}`}
              classes="secondary"
              size="large"
              onClick={() => addProduct(Product)}
            />
          )}
          {hasProduct(Product.Id) && (
            <Button
              text={`${t("scenes.extras.remove")}`}
              classes="secondary"
              size="large"
              onClick={() => removeProduct(Product.Id)}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default RoomExtraProductCard;
